// src/utils/createPdf.js
import jsPDF from "jspdf";

export function createPdfBlobUrl(recs, summaryBoxes) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Paleta de colores del CSS
  const COLORS = {
    white:      "#FFFFFF", 
    delilife:   "#00be9c",
    azulClaro:  "#d5e9fc",
    azulOscuro: "#042330",
    grisSec:    "#76797c",
    grisFondo:  "#f1f6f4",
    azul:       "#1069f1",
    amarillo:   "#ffe951"
  };

  // Fondo de página
  doc.setFillColor(COLORS.grisFondo);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  // 0) Header: barra azul oscura, logo y texto centrados
  const headerBarHeight = 30;
  doc.setFillColor(COLORS.azulOscuro);
  doc.rect(0, 0, pageWidth, headerBarHeight, "F");

  // Ajuste logo + texto dentro del mismo header
  const textFontSize = 10;
  const logoSize = 12;
  const totalHeight = logoSize + textFontSize;
  const startY = (headerBarHeight - totalHeight) / 2;
  // Agregar logo192.png (desde public) centrado
  doc.addImage(
    '/10.png', 'PNG',
    (pageWidth - logoSize) / 2,
    startY,
    logoSize,
    logoSize
  );
  // Texto debajo del logo
  doc.setFontSize(textFontSize);
  doc.setTextColor(COLORS.white);
  doc.text(
    'DeliLifeWellness',
    pageWidth / 2,
    startY + logoSize + textFontSize,
    { align: 'center' }
  );

  // Márgenes y ancho máximo para contenido, bajo el header
  const margin = 20;
  const maxWidth = pageWidth - margin * 2;

  // Fuente base y color de texto
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(COLORS.azulOscuro);

  // 1) TÍTULO con wrap y centrado
  const titleFontSize = 22;
  doc.setFontSize(titleFontSize);
  doc.setTextColor(COLORS.delilife);
  const title =
    "Tus resultados de la Evaluación de Riesgo en Salud (Health Risk Assessment)";
  const titleLines = doc.splitTextToSize(title, maxWidth);
  const titleX = pageWidth / 2;
  const titleY = headerBarHeight + margin;
  doc.text(titleLines, titleX, titleY, { align: "center" });

  // Altura del título
  const lineHeightFactor = doc.getLineHeightFactor();
  const titleLineHeight = titleFontSize * lineHeightFactor;
  const titleHeight = titleLines.length * titleLineHeight;

  // 2) Resumen en tabla
  const cols = 2;
  const rows = Math.ceil(summaryBoxes.length / cols);
  const cellWidth = (pageWidth - margin * 2) / cols;
  const tableX = margin;

  const secTitleFontSize = 16;
  const boxFontSize = 12;
  const tablePaddingBetween = 10;
  const tableSpacing = 5;
  const horizontalGap = 10;

  let currentY = titleY + titleHeight + tablePaddingBetween;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const idx = row * cols + col;
      if (idx >= summaryBoxes.length) break;

      const { title, textRisk, boxColor, textColor } = summaryBoxes[idx];
      const x = tableX + col * cellWidth;
      const y = currentY;

      // 2.1) Título de sección centrado
      doc.setFontSize(secTitleFontSize);
      doc.setTextColor(COLORS.azulOscuro);
      const titleXCell = x + cellWidth / 2;
      doc.text(title, titleXCell, y, { align: "center" });

      // 2.2) Recuadro con ancho fijo y color dinámico
      const boxX = x + horizontalGap / 2;
      const boxW = cellWidth - horizontalGap;
      const boxH = boxFontSize * lineHeightFactor + 8;
      const boxY = y + 5;

      doc.setFillColor(boxColor);
      doc.setDrawColor(boxColor);
      doc.rect(boxX, boxY, boxW, boxH, "FD");

      // 2.3) Texto centrado dentro del recuadro
      doc.setFontSize(boxFontSize);
      doc.setTextColor(textColor || COLORS.azulOscuro);
      const textX = boxX + boxW / 2;
      const textY = boxY + boxH / 2 + boxFontSize * 0.35;
      doc.text(textRisk, textX, textY, { align: "center" });
    }
    // Avanzar Y a la siguiente fila
    currentY +=
      secTitleFontSize * lineHeightFactor +
      5 +
      (boxFontSize * lineHeightFactor + 8) +
      tableSpacing;
  }

  // 3) Nueva página para recomendaciones
  doc.addPage();

  // Header nueva página
  const recHeaderHeight = 20;
  doc.setFillColor(COLORS.azulOscuro);
  doc.rect(0, 0, pageWidth, recHeaderHeight, "F");
  doc.setFontSize(16);
  doc.setTextColor(COLORS.white);
  doc.text(
    "Recomendaciones Detalladas",
    margin,
    recHeaderHeight / 2 + 4
  );

  // 4) Cuerpo de recomendaciones
  let yPos = recHeaderHeight + 15;
  const contentFontSize = 12;
  const sectionHeaderPadding = 4;
  const sectionSpacing = 5;

  recs.forEach((rec) => {
    const titleLinesSec = doc.splitTextToSize(rec.title, maxWidth);
    const sectionHeaderHeight = titleLinesSec.length * secTitleFontSize * lineHeightFactor;

    // Header sección
    doc.setFillColor(COLORS.azulOscuro);
    doc.rect(margin, yPos, maxWidth, sectionHeaderHeight, "F");
    doc.setFontSize(secTitleFontSize);
    doc.setTextColor(COLORS.white);
    doc.text(
      titleLinesSec,
      margin + sectionHeaderPadding,
      yPos + sectionHeaderPadding + secTitleFontSize * lineHeightFactor / 2,
      { align: "left" }
    );

    yPos += sectionHeaderHeight + 4;

    // Texto detalle
    doc.setFontSize(contentFontSize);
    doc.setTextColor(COLORS.azulOscuro);
    const textLines = doc.splitTextToSize(rec.text, maxWidth);
    textLines.forEach(line => {
      if (yPos > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
      }
      doc.text(line, margin, yPos);
      yPos += contentFontSize * lineHeightFactor;
    });
    yPos += sectionSpacing;
  });

  return doc.output("bloburl");
}

const CGS_COLORS = {
  white: "#FFFFFF",
  delilife: "#00be9c",
  azulOscuro: "#042330",
  grisSec: "#76797c",
  grisFondo: "#f1f6f4",
  amarillo: "#ffe951",
  amarilloProf: "#d4aa00",
  naranja: "#e78b2d",
  rojo: "#dc3545",
  azul: "#1069f1",
  border: "#d7dde2",
  borderSoft: "#e8edf1",
  text: "#042330",
  toneGreenSoft: "#e8fbf7",
  toneYellowSoft: "#fff8d8",
  toneOrangeSoft: "#fff0e1",
  toneRedSoft: "#fdecef",
  toneNeutralSoft: "#eaf2ff"
};

const toNumberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const cgsModuleRecommendationsFallback = {
  green: [
    "Mantén los hábitos que hoy te están ayudando y monitorea cambios en tu rutina.",
    "Si notas variaciones en sueño, ánimo o estrés, repite la evaluación para comparar evolución."
  ],
  yellow: [
    "Observa qué situaciones disparan más malestar y registra patrones (horarios, carga, descanso).",
    "Pequeños ajustes en sueño, pausas y actividad física pueden ayudar antes de que aumente la carga."
  ],
  orange: [
    "Conviene dar seguimiento cercano a este resultado y apoyarte en una red de confianza.",
    "Si el malestar interfiere con tu rutina, busca orientación profesional para una valoración personalizada."
  ],
  red: [
    "Este resultado sugiere una afectación importante y vale la pena atenderlo cuanto antes.",
    "Te recomendamos buscar apoyo profesional para una interpretación clínica y un plan de acompañamiento."
  ],
  neutral: [
    "No se pudo identificar la categoría del resultado con la información recibida.",
    "Intenta repetir la consulta o comparte el resultado con el equipo para validarlo."
  ]
};

const cgsModuleGuidance = {
  bai: {
    levels: [
      {
        range: "0-21",
        level: "Muy baja",
        meaning:
          "La persona experimenta síntomas de ansiedad mínimos o ausentes. Su sistema nervioso responde de manera equilibrada ante situaciones cotidianas. No representa interferencia en su funcionamiento diario.",
        recommendations: [
          "Mantener hábitos de autocuidado: actividad física, sueño regular y alimentación balanceada.",
          "Continuar con estrategias de manejo del estrés que ya funcionan (meditación, tiempo en naturaleza, hobbies).",
          "Reevaluación preventiva cada 6 meses."
        ]
      },
      {
        range: "22-35",
        level: "Moderada",
        meaning:
          "Presencia notable de síntomas físicos y cognitivos de ansiedad (tensión muscular, preocupación frecuente, dificultad para relajarse). Puede comenzar a interferir en el rendimiento laboral, las relaciones y la calidad del sueño.",
        recommendations: [
          "Iniciar acompañamiento psicológico con enfoque en Terapia Cognitivo-Conductual (TCC).",
          "Incorporar técnicas de respiración diafragmática y mindfulness de forma regular.",
          "Revisar carga laboral y factores de estrés identificables en el entorno de trabajo.",
          "Evaluar si el patrón persiste más de 2 semanas para derivar a especialista."
        ]
      },
      {
        range: ">=36",
        level: "Severa",
        meaning:
          "Ansiedad intensa que afecta significativamente el funcionamiento global. Pueden presentarse crisis de ansiedad, evitación de situaciones, síntomas somáticos (palpitaciones, sudoración, mareos) y deterioro en relaciones interpersonales y desempeño laboral.",
        recommendations: [
          "Derivación inmediata a psicólogo y/o psiquiatra para evaluación clínica completa.",
          "Considerar evaluación médica para descartar causas orgánicas.",
          "Implementar plan de atención en crisis dentro del programa de salud mental empresarial.",
          "Ajuste de responsabilidades laborales mientras dure el tratamiento."
        ]
      }
    ]
  },
  psqi: {
    levels: [
      {
        range: "0-4",
        level: "Buena calidad",
        meaning:
          "El sueño es reparador y suficiente. La persona concilia el sueño con facilidad, duerme el tiempo adecuado y se despierta sintiéndose descansada. No hay interferencia en la vida diaria por problemas relacionados con el descanso.",
        recommendations: [
          "Preservar rutinas de sueño consistentes (horario regular de acostarse y levantarse).",
          "Evitar pantallas 30-60 min antes de dormir como medida preventiva.",
          "Incluir espacio físico de descanso dentro de la jornada laboral."
        ]
      },
      {
        range: ">5",
        level: "Alteración",
        meaning:
          "Existe una alteración clínicamente significativa del sueño. Puede manifestarse como dificultad para conciliar o mantener el sueño, despertares nocturnos frecuentes, sueño no reparador o somnolencia diurna excesiva. Impacta directamente en el estado de ánimo, la concentración y la productividad.",
        recommendations: [
          "Aplicar protocolo de higiene del sueño: ambiente oscuro, fresco y sin ruido; eliminar cafeína después de las 2 pm.",
          "Explorar posible presencia de insomnio crónico, apnea del sueño u otros trastornos con médico o psicólogo.",
          "Incorporar técnicas de relajación progresiva o meditación guiada antes de dormir.",
          "Revisar si el trabajo en horarios atípicos, el estrés laboral o el uso excesivo de dispositivos están contribuyendo al problema."
        ]
      }
    ]
  },
  pss: {
    levels: [
      {
        range: "0-19",
        level: "Bajo - Moderado bajo",
        meaning:
          "La persona percibe que tiene control sobre su vida y sus demandas cotidianas. Los niveles de estrés son manejables y no representan una amenaza para su bienestar. Cuenta con recursos de afrontamiento funcionales.",
        recommendations: [
          "Fortalecer las estrategias de afrontamiento que ya usa (red de apoyo, organización, ejercicio).",
          "Participar en actividades de bienestar preventivo: talleres de resiliencia, pausas activas.",
          "Reevaluación semestral para detectar cambios ante nuevas exigencias laborales."
        ]
      },
      {
        range: "20-25",
        level: "Moderado",
        meaning:
          "Se percibe un nivel de estrés que empieza a ser demandante. La persona puede sentir que sus recursos no son suficientes para manejar sus responsabilidades. Pueden aparecer señales como irritabilidad, fatiga, dificultad para desconectarse del trabajo o tensión física frecuente.",
        recommendations: [
          "Iniciar sesiones de coaching de bienestar o psicoeducación sobre manejo del estrés.",
          "Enseñar técnicas de gestión del tiempo y priorización de tareas.",
          "Revisar la carga de trabajo con el área de Recursos Humanos para identificar factores organizacionales.",
          "Incorporar práctica regular de mindfulness o actividad física (mínimo 3 veces por semana)."
        ]
      },
      {
        range: ">25",
        level: "Elevado",
        meaning:
          "El estrés percibido es alto y crónico. La persona siente que los eventos de su vida están fuera de su control y que no puede manejar las demandas que enfrenta. Esto aumenta el riesgo de burnout, enfermedades físicas, trastornos del sueño y deterioro en las relaciones interpersonales.",
        recommendations: [
          "Iniciar psicoterapia individual de forma inmediata (TCC, ACT o terapia de reducción de estrés basada en mindfulness - MBSR).",
          "Evaluación de factores de riesgo psicosocial en el entorno laboral (NOM-035).",
          "Revisar la posibilidad de ajuste de responsabilidades, licencia temporal o apoyo adicional.",
          "Monitoreo mensual de indicadores de salud mental hasta estabilización."
        ]
      }
    ]
  },
  bdi: {
    levels: [
      {
        range: "0-13",
        level: "Mínima",
        meaning:
          "Ausencia o presencia mínima de síntomas depresivos. El estado de ánimo es estable, la persona mantiene motivación, disfruta de actividades y tiene una percepción positiva de sí misma y del futuro.",
        recommendations: [
          "Mantener redes de apoyo social activas dentro y fuera del trabajo.",
          "Participar en actividades de bienestar emocional preventivo (talleres, cafés con sentido, sesiones de mindfulness).",
          "Seguimiento anual de bienestar emocional."
        ]
      },
      {
        range: "14-19",
        level: "Leve",
        meaning:
          "Presencia de síntomas depresivos leves que pueden incluir tristeza ocasional, menor energía, ligera pérdida de interés en actividades habituales o pensamientos pesimistas esporádicos. No impide el funcionamiento, pero puede afectar la calidad de vida si no se atiende.",
        recommendations: [
          "Iniciar psicoeducación sobre bienestar emocional y reconocimiento de señales tempranas.",
          "Motivar la actividad física regular, que tiene efecto comprobado sobre el estado de ánimo.",
          "Ofrecer espacio de escucha activa o sesiones de apoyo psicológico breve.",
          "Monitoreo cada 4-6 semanas para detectar evolución."
        ]
      },
      {
        range: "20-28",
        level: "Moderada",
        meaning:
          "Los síntomas depresivos son frecuentes e interfieren notablemente en el funcionamiento diario. Pueden presentarse: tristeza persistente, fatiga, dificultad para concentrarse, sentimientos de culpa o inutilidad, cambios en apetito o sueño. El rendimiento laboral y las relaciones personales se ven afectadas.",
        recommendations: [
          "Derivación a psicólogo clínico para inicio de psicoterapia estructurada (TCC o terapia interpersonal).",
          "Evaluación psiquiátrica para determinar si se requiere apoyo farmacológico.",
          "Comunicar al área de Recursos Humanos para ajuste temporal de carga laboral.",
          "Seguimiento quincenal hasta mejoría sostenida."
        ]
      },
      {
        range: "29-63",
        level: "Grave",
        meaning:
          "Depresión severa con impacto profundo en todas las áreas de vida. Síntomas intensos y persistentes: incapacidad para realizar actividades cotidianas, sentimientos de desesperanza, pensamientos negativos recurrentes sobre sí mismo o el futuro. Posible riesgo de ideación suicida que debe ser evaluado de inmediato.",
        recommendations: [
          "Atención psiquiátrica urgente y evaluación de riesgo de manera inmediata.",
          "Activar protocolo de contención emocional de la empresa si existe.",
          "No dejar a la persona sin red de apoyo; involucrar a contacto de confianza o familiar cercano.",
          "Baja laboral temporal si el médico tratante lo considera necesario.",
          "Seguimiento intensivo semanal hasta estabilización clínica."
        ]
      }
    ]
  }
};

const cgsModuleDefs = [
  {
    key: "bai",
    title: "Cuestionario de Ansiedad de Beck",
    description:
      "Valorar la severidad de los síntomas de ansiedad, y discriminar de manera fiable entre ansiedad y depresión. Instrucciones: indique para cada uno de los siguientes síntomas el grado de aplicación en que se ha visto afectado por cada uno de ellos durante la última semana y en el momento actual.",
    ranges: ["0-21", "22-35", ">=36"]
  },
  {
    key: "bdi",
    title: "Inventario de Depresión de Beck (BDI)",
    description:
      "Cuestionario para identificar y medir la gravedad de síntomas típicos de la depresión en adultos y adolescentes. Permite diferenciar a un paciente con un trastorno depresivo mayor de aquel que simplemente evidencia un ánimo bajo.",
    ranges: ["0-13", "14-19", "20-28", "29-63"]
  },
  {
    key: "psqi",
    title: "Índice de Calidad de Sueño de Pittsburgh",
    description:
      "Cuestionario que mide la calidad de sueño y sus alteraciones en el último mes. Evalúa calidad de sueño subjetiva, latencia de sueño, duración del dormir, eficiencia de sueño, alteraciones de sueño, uso de medicamentos para dormir y disfunción diurna.",
    ranges: ["0-4", ">5"]
  },
  {
    key: "pss",
    title: "Escala de Estrés Percibido",
    description:
      "Escala diseñada para medir el grado en que las situaciones de la vida son evaluadas como estresantes. Los ítems evalúan el grado en que las personas encuentran su vida impredecible, incontrolable o sobrecargada, componentes centrales de la experiencia de estrés.",
    ranges: ["0-19", "20-25", ">25"]
  }
];

function getCgsSeverityTone(index, totalRanges) {
  if (index < 0) return "neutral";
  if (totalRanges <= 2) return index === 0 ? "green" : "red";
  if (totalRanges === 3) {
    if (index === 0) return "green";
    if (index === totalRanges - 1) return "red";
    return "yellow";
  }

  if (index === 0) return "green";
  if (index === totalRanges - 1) return "red";
  if (index === totalRanges - 2) return "orange";
  return "yellow";
}

function getCgsToneColors(tone) {
  switch (tone) {
    case "green":
      return { strong: CGS_COLORS.delilife, soft: CGS_COLORS.toneGreenSoft };
    case "yellow":
      return { strong: CGS_COLORS.amarilloProf, soft: CGS_COLORS.toneYellowSoft };
    case "orange":
      return { strong: CGS_COLORS.naranja, soft: CGS_COLORS.toneOrangeSoft };
    case "red":
      return { strong: CGS_COLORS.rojo, soft: CGS_COLORS.toneRedSoft };
    default:
      return { strong: CGS_COLORS.azul, soft: CGS_COLORS.toneNeutralSoft };
  }
}

function isTotalInTextRange(total, rangeText) {
  if (typeof total !== "number" || typeof rangeText !== "string") return false;

  const normalizedRange = rangeText.replace(/\s+/g, "");
  const numberPattern = "(-?\\d+(?:\\.\\d+)?)";

  const betweenMatch = normalizedRange.match(new RegExp(`^${numberPattern}-${numberPattern}$`));
  if (betweenMatch) {
    const min = Number(betweenMatch[1]);
    const max = Number(betweenMatch[2]);
    return total >= min && total <= max;
  }

  const gteMatch = normalizedRange.match(new RegExp(`^>=${numberPattern}$`));
  if (gteMatch) return total >= Number(gteMatch[1]);

  const lteMatch = normalizedRange.match(new RegExp(`^<=${numberPattern}$`));
  if (lteMatch) return total <= Number(lteMatch[1]);

  const gtMatch = normalizedRange.match(new RegExp(`^>${numberPattern}$`));
  if (gtMatch) return total > Number(gtMatch[1]);

  const ltMatch = normalizedRange.match(new RegExp(`^<${numberPattern}$`));
  if (ltMatch) return total < Number(ltMatch[1]);

  const exact = Number(normalizedRange);
  return Number.isFinite(exact) ? total === exact : false;
}

function getGuidanceLevels(moduleKey) {
  const levels = cgsModuleGuidance[moduleKey]?.levels;
  return Array.isArray(levels) ? levels : [];
}

function getGuidanceLevel(moduleKey, total, fallbackIndex) {
  const levels = getGuidanceLevels(moduleKey);
  const byTotal = levels.find((level) => isTotalInTextRange(total, level?.range));
  if (byTotal) return byTotal;

  const parsedFallbackIndex = Number(fallbackIndex);
  if (
    Number.isInteger(parsedFallbackIndex) &&
    parsedFallbackIndex >= 0 &&
    parsedFallbackIndex < levels.length
  ) {
    return levels[parsedFallbackIndex];
  }

  return null;
}

function getSelectedRangeIndexFromStrings(total, rangeLabels, fallbackIndex) {
  const byTotal = rangeLabels.findIndex((label) => isTotalInTextRange(total, label));
  if (byTotal >= 0) return byTotal;

  const parsedFallbackIndex = Number(fallbackIndex);
  if (
    Number.isInteger(parsedFallbackIndex) &&
    parsedFallbackIndex >= 0 &&
    parsedFallbackIndex < rangeLabels.length
  ) {
    return parsedFallbackIndex;
  }

  return -1;
}

function splitBulletLines(doc, items, width) {
  const rows = [];
  items.forEach((item) => {
    const wrapped = doc.splitTextToSize(item, width);
    if (!wrapped.length) {
      rows.push([""]);
      return;
    }
    rows.push(wrapped);
  });
  return rows;
}

function getCgsSectionComputed(section, result) {
  const labels = Array.isArray(result?.labels) ? result.labels : [];
  const total = toNumberOrNull(result?.total);
  const selectedIndex = getSelectedRangeIndexFromStrings(total, section.ranges, result?.indexResult);
  const guidanceLevels = getGuidanceLevels(section.key);
  const selectedGuidance = getGuidanceLevel(section.key, total, selectedIndex);
  const currentLabel =
    selectedIndex >= 0
      ? labels[selectedIndex] || guidanceLevels[selectedIndex]?.level || "Sin etiqueta"
      : selectedGuidance?.level || "Sin clasificación";
  const tone = getCgsSeverityTone(selectedIndex, section.ranges.length);
  const meaningText =
    selectedGuidance?.meaning ||
    (typeof total === "number"
      ? `Tu puntuación actual es ${total} y se ubica en la categoría "${currentLabel}". Este resultado es orientativo y debe interpretarse junto con tu contexto personal.`
      : "No fue posible calcular una categoría con la información recibida. Revisa el resultado y vuelve a intentarlo.");
  const recommendations =
    selectedGuidance?.recommendations?.length > 0
      ? selectedGuidance.recommendations
      : cgsModuleRecommendationsFallback[tone] || cgsModuleRecommendationsFallback.neutral;

  return {
    labels,
    total,
    selectedIndex,
    guidanceLevels,
    selectedGuidance,
    currentLabel,
    tone,
    meaningText,
    recommendations
  };
}

function fillCgsPageBackground(doc) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFillColor(CGS_COLORS.grisFondo);
  doc.rect(0, 0, pageW, pageH, "F");
}

function loadImageData(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL("image/png"),
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function estimateSectionHeight(doc, section, result, contentWidth) {
  const computed = getCgsSectionComputed(section, result);
  const cardPadding = 8;
  const topBarH = 3;
  const blockGap = 4;
  const tableHeadH = 8;
  const rowH = 7.5;
  const badgeH = 16;
  const infoTitleH = 6;

  const innerW = contentWidth - cardPadding * 2;
  const titleW = innerW;
  const descW = innerW;
  const infoBoxW = innerW - 8;
  const meaningTextW = infoBoxW - 6;
  const recTextW = infoBoxW - 8;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  const titleLines = doc.splitTextToSize(section.title, titleW);
  const titleH = Math.max(6, titleLines.length * 5.2);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.4);
  const descLines = doc.splitTextToSize(section.description, descW);
  const descH = Math.max(4.5, descLines.length * 3.8);

  const rowsHeight = section.ranges.length * rowH;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.4);
  const meaningLines = doc.splitTextToSize(computed.meaningText, meaningTextW);
  const meaningBodyH = Math.max(5, meaningLines.length * 3.8);
  const meaningBoxH = 5 + infoTitleH + 2 + meaningBodyH + 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.2);
  const recBulletRows = splitBulletLines(doc, computed.recommendations, recTextW);
  const recLinesCount = recBulletRows.reduce((acc, lines) => acc + Math.max(1, lines.length), 0);
  const recBodyH = recLinesCount * 3.6 + Math.max(0, recBulletRows.length - 1) * 1.2;
  const recBoxH = 5 + infoTitleH + 2 + recBodyH + 4;

  return (
    topBarH +
    cardPadding +
    titleH +
    2 +
    descH +
    blockGap +
    badgeH +
    blockGap +
    6 + // eyebrow
    tableHeadH +
    rowsHeight +
    5 + // total line
    blockGap +
    meaningBoxH +
    blockGap +
    recBoxH +
    cardPadding
  );
}

function drawPdfHeader(doc, pageWidth, companyName, logo) {
  const headerH = 22;
  doc.setFillColor(CGS_COLORS.azulOscuro);
  doc.rect(0, 0, pageWidth, headerH, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(CGS_COLORS.white);
  doc.text(companyName || "Deli Life Wellness", 10, 14);

  if (logo) {
    const maxH = 14;
    const ratio = logo.width / logo.height;
    const drawH = maxH;
    const drawW = drawH * ratio;
    const x = pageWidth / 2 - drawW / 2;
    const y = (headerH - drawH) / 2;
    doc.addImage(logo.dataUrl, "PNG", x, y, drawW, drawH);
  }

  return headerH;
}

function drawInfoBox(doc, { x, y, w, title, bodyLines, tone, isList = false }) {
  const toneColors = getCgsToneColors(tone);
  const pad = 4;
  const titleH = 6;
  const lineH = isList ? 3.6 : 3.8;

  let bodyHeight = 0;
  if (isList) {
    bodyLines.forEach((bullet) => {
      bodyHeight += Math.max(1, bullet.length) * lineH;
      bodyHeight += 1.2;
    });
    if (bodyLines.length > 0) bodyHeight -= 1.2;
  } else {
    bodyHeight = Math.max(1, bodyLines.length) * lineH;
  }

  const boxH = pad + titleH + 2 + bodyHeight + pad;

  doc.setFillColor(toneColors.soft);
  doc.setDrawColor(CGS_COLORS.borderSoft);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, boxH, 2, 2, "FD");

  doc.setFillColor(toneColors.strong);
  doc.circle(x + pad + 1.5, y + pad + 2.1, 1.2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.6);
  doc.setTextColor(CGS_COLORS.text);
  doc.text(title, x + pad + 5, y + pad + 3.3);

  let cursorY = y + pad + titleH + 1.8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(isList ? 8.2 : 8.4);
  doc.setTextColor(CGS_COLORS.text);

  if (isList) {
    bodyLines.forEach((bullet) => {
      bullet.forEach((line, lineIndex) => {
        if (lineIndex === 0) {
          doc.setFillColor(toneColors.strong);
          doc.circle(x + pad + 1.6, cursorY - 1.1, 0.55, "F");
          doc.text(line, x + pad + 4, cursorY);
        } else {
          doc.text(line, x + pad + 4, cursorY);
        }
        cursorY += lineH;
      });
      cursorY += 1.2;
    });
  } else {
    doc.text(bodyLines, x + pad, cursorY);
  }

  return boxH;
}

function drawCgsSection(doc, section, result, x, y, w, order) {
  const computed = getCgsSectionComputed(section, result);
  const toneColors = getCgsToneColors(computed.tone);
  const sectionHeight = estimateSectionHeight(doc, section, result, w);
  const cardPad = 8;
  const barH = 3;
  const contentX = x + cardPad;
  const contentW = w - cardPad * 2;

  doc.setFillColor(CGS_COLORS.white);
  doc.setDrawColor(CGS_COLORS.border);
  doc.setLineWidth(0.35);
  doc.roundedRect(x, y, w, sectionHeight, 2.5, 2.5, "FD");

  doc.setFillColor(toneColors.strong);
  doc.roundedRect(x, y, w, barH, 2.5, 2.5, "F");
  doc.rect(x, y + 1.5, w, 1.5, "F");

  let cursorY = y + barH + cardPad;

  doc.setFillColor(toneColors.soft);
  doc.setDrawColor(toneColors.strong);
  doc.roundedRect(contentX, cursorY - 4.8, 8, 8, 2, 2, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(toneColors.strong);
  doc.text(String(order), contentX + 4, cursorY + 0.4, { align: "center" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(CGS_COLORS.text);
  const titleLines = doc.splitTextToSize(section.title, contentW - 12);
  doc.text(titleLines, contentX + 11, cursorY);
  cursorY += Math.max(6, titleLines.length * 5.2) + 1.2;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.4);
  doc.setTextColor(CGS_COLORS.grisSec);
  const descLines = doc.splitTextToSize(section.description, contentW);
  doc.text(descLines, contentX, cursorY);
  cursorY += Math.max(4.5, descLines.length * 3.8) + 4;

  const badgeH = 16;
  doc.setFillColor(toneColors.soft);
  doc.setDrawColor(toneColors.strong);
  doc.setLineWidth(0.35);
  doc.roundedRect(contentX, cursorY, contentW, badgeH, 2, 2, "FD");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.8);
  doc.setTextColor(CGS_COLORS.grisSec);
  doc.text("Total obtenido", contentX + 4, cursorY + 4.2);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(CGS_COLORS.text);
  doc.text(String(computed.total ?? "N/A"), contentX + 4, cursorY + 10.3);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.6);
  doc.setTextColor(toneColors.strong);
  const levelText = computed.currentLabel || "Sin clasificación";
  const levelLines = doc.splitTextToSize(levelText, contentW - 35);
  doc.text(levelLines, contentX + 32, cursorY + 7.6);
  cursorY += badgeH + 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(CGS_COLORS.grisSec);
  doc.text("Rangos de referencia", contentX, cursorY);
  cursorY += 3;

  const tableX = contentX;
  const tableW = contentW;
  const col1W = tableW * 0.37;
  const col2W = tableW - col1W;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.4);
  doc.setTextColor(CGS_COLORS.text);
  doc.setFillColor("#f5f8fb");
  doc.setDrawColor(CGS_COLORS.borderSoft);
  doc.rect(tableX, cursorY, tableW, 8, "FD");
  doc.text("Puntuación total", tableX + col1W / 2, cursorY + 5, { align: "center" });
  doc.text("Indicación", tableX + col1W + col2W / 2, cursorY + 5, { align: "center" });
  cursorY += 8;

  section.ranges.forEach((rangeLabel, idx) => {
    const isSelected = computed.selectedIndex === idx;
    const rowTone = getCgsSeverityTone(idx, section.ranges.length);
    const rowToneColors = getCgsToneColors(rowTone);
    const rowY = cursorY;
    const rowH = 7.5;
    const rowLabel = computed.labels[idx] || computed.guidanceLevels[idx]?.level || "Sin etiqueta";

    if (isSelected) {
      doc.setFillColor(rowToneColors.soft);
      doc.setDrawColor(rowToneColors.strong);
      doc.setLineWidth(0.45);
    } else {
      doc.setFillColor(CGS_COLORS.white);
      doc.setDrawColor(CGS_COLORS.borderSoft);
      doc.setLineWidth(0.25);
    }

    doc.rect(tableX, rowY, col1W, rowH, "FD");
    doc.rect(tableX + col1W, rowY, col2W, rowH, "FD");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.4);
    doc.setTextColor(isSelected ? rowToneColors.strong : CGS_COLORS.text);
    doc.text(`${isSelected ? "\u25B6 " : ""}${rangeLabel}`, tableX + 2.2, rowY + 5);
    doc.text(rowLabel, tableX + col1W + 2.2, rowY + 5);
    cursorY += rowH;
  });

  cursorY += 5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.6);
  doc.setTextColor(CGS_COLORS.text);
  doc.text(`Total obtenido: ${computed.total ?? "N/A"}`, contentX, cursorY);
  cursorY += 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.4);
  const meaningLines = doc.splitTextToSize(computed.meaningText, contentW - 14);
  const meaningBoxH = drawInfoBox(doc, {
    x: contentX,
    y: cursorY,
    w: contentW,
    title: "Que significa",
    bodyLines: meaningLines,
    tone: computed.tone,
    isList: false
  });
  cursorY += meaningBoxH + 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.2);
  const recBulletRows = splitBulletLines(doc, computed.recommendations, contentW - 16);
  drawInfoBox(doc, {
    x: contentX,
    y: cursorY,
    w: contentW,
    title: "Recomendacion general",
    bodyLines: recBulletRows,
    tone: computed.tone,
    isList: true
  });
}

export async function createCgsPdfBlobUrl({
  results,
  companyName = "Deli Life Wellness",
  logoUrl = "/logo512.png"
}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const contentW = pageW - margin * 2;
  const logo = await loadImageData(logoUrl);

  fillCgsPageBackground(doc);
  let headerH = drawPdfHeader(doc, pageW, companyName, logo);
  let currentY = headerH + 10;
  let sectionsInCurrentPage = 0;
  const maxSectionsPerPage = 2;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(CGS_COLORS.delilife);
  doc.text("Tus resultados:", pageW / 2, currentY, { align: "center" });
  currentY += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(CGS_COLORS.grisSec);
  doc.text(
    "Revisa tu resultado por dimension y consulta los rangos de referencia de cada escala.",
    pageW / 2,
    currentY,
    { align: "center" }
  );
  currentY += 8;

  cgsModuleDefs.forEach((section, index) => {
    const sectionResult = results?.[section.key] || {};
    const sectionHeight = estimateSectionHeight(doc, section, sectionResult, contentW);

    const exceedsPage = currentY + sectionHeight > pageH - margin;
    const reachedPageCap = sectionsInCurrentPage >= maxSectionsPerPage;

    if (index > 0 && (exceedsPage || reachedPageCap)) {
      doc.addPage();
      fillCgsPageBackground(doc);
      headerH = drawPdfHeader(doc, pageW, companyName, logo);
      currentY = headerH + 8;
      sectionsInCurrentPage = 0;
    }

    drawCgsSection(doc, section, sectionResult, margin, currentY, contentW, index + 1);
    currentY += sectionHeight + 6;
    sectionsInCurrentPage += 1;
  });

  return doc.output("bloburl");
}
